import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faAngleLeft, faAngleRight, faCopy, faPen, faSquarePlus} from '@fortawesome/free-solid-svg-icons';
import {forkJoin} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {environment} from '../../../../environments/environment';

import {RegisterService} from '../../../services/register.service';
import {AuthService} from '../../../services/auth.service';
import {FieldService} from '../../../services/field.service';
import {GenericSearchComponent} from '../../../shared/search-suggestion/search-suggestion.component';
import {DEFAULT_TABLE_PAGE_SIZE_OPTIONS} from '../../../shared/constants/pagination.constants';
import {VtePaginationComponent} from '../../../shared/vte/vte-pagination/vte-pagination.component';

@Component({
    selector: 'app-register-list',
    imports: [CommonModule, FormsModule, FontAwesomeModule, GenericSearchComponent, VtePaginationComponent],
    templateUrl: './register-list.component.html',
    styleUrl: './register-list.component.css'
})
export class RegisterListComponent implements OnInit {
    readonly pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;
  registers: any[] = [];
  locations: any[] = [];
  fieldCounts: Record<number, number> = {};
  stepsCount: Record<number, number> = {};

  recordsPerPage: number = 10;
  currentPage: number = 1;
  nextPage: string | null = null;
  previousPage: string | null = null;
  totalRecords: number = 0;

  searchText: string = '';
  selectedLocation: number | null = null;
  isAdminUser: boolean = false;
  searchExtras: Record<string, any> = {};
  visitorApiBaseUrl: string = environment.baseUrl.replace('/v1/', '/api/v1/');

  constructor(
    private registerService: RegisterService,
    private fieldService: FieldService,
    private auth: AuthService,
    private router: Router,
    private toastr: ToastrService,
    library: FaIconLibrary
  ) {
    library.addIcons(faSquarePlus, faPen, faCopy, faAngleLeft, faAngleRight);
  }

  ngOnInit(): void {
    const roles = this.getUserRoles();
      const isPlatformSuperuser = this.auth.isSuperUser();
      this.isAdminUser = roles.includes('Admin') && !isPlatformSuperuser;
      if (isPlatformSuperuser) {
      this.router.navigate(['/dashboard']);
      return;
    }
    if (!this.isAdminUser) {
      this.router.navigate(['/visitor-management/select-register']);
      return;
    }
    this.fetchLocations();
    this.updateSearchExtras();
    this.fetchRegisters(this.currentPage, this.recordsPerPage);
  }

  private getUserRoles(): string[] {
    const userInfo = this.auth.getCurrentUserInfo();
    const role = userInfo?.role;
    if (Array.isArray(role)) {
      return role;
    }
    return role ? [role] : [];
  }

  fetchLocations(): void {
    this.registerService.listLocations(1, 200).subscribe({
      next: (res: any) => {
        this.locations = res?.results ?? res ?? [];
      }
    });
  }

  fetchRegisters(page: number, pageSize: number, searchText: string = ''): void {
    this.registerService.getRegisters(page, pageSize, searchText, this.selectedLocation || undefined).subscribe({
      next: (res: any) => {
        this.registers = res?.results || [];
        this.totalRecords = res?.count ?? 0;
        this.nextPage = res?.next ?? null;
        this.previousPage = res?.previous ?? null;
        this.buildStepCounts();
        this.loadFieldCounts();
      }
    });
  }

  onResults(event: any): void {
    this.searchText = event.searchText || '';
    this.registers = event.results || [];
    this.totalRecords = event.totalRecords || 0;
    this.nextPage = event.nextPage || null;
    this.previousPage = event.previousPage || null;
    this.currentPage = 1;
    this.buildStepCounts();
    this.loadFieldCounts();
  }

  buildStepCounts(): void {
    this.stepsCount = {};
    this.registers.forEach(register => {
      const steps = Array.isArray(register.steps_config) ? register.steps_config.length : 0;
      this.stepsCount[register.id] = steps || 1;
    });
  }

  loadFieldCounts(): void {
    if (!this.registers.length) {
      this.fieldCounts = {};
      return;
    }
    const requests = this.registers.map(register =>
      this.fieldService.listFieldsByRegister(register.id)
    );
    forkJoin(requests).subscribe({
      next: (responses: any[]) => {
        responses.forEach((fields, index) => {
          const registerId = this.registers[index].id;
          this.fieldCounts[registerId] = Array.isArray(fields) ? fields.length : 0;
        });
      }
    });
  }

  createRegister(): void {
    this.router.navigate(['/visitor-management/registers/create']);
  }

  editRegister(registerId: number): void {
    this.router.navigate([`/visitor-management/registers/${registerId}/edit`]);
  }


  copyRegister(register: any): void {
    this.registerService.getRegister(register.id).subscribe({
      next: (registerDetail: any) => {
        this.fieldService.listFieldsByRegister(register.id).subscribe({
          next: (fields: any[]) => {
            const payload = {
              name: `${registerDetail.name} Copy`,
              description: registerDetail.description,
              scope_type: registerDetail.scope_type,
              location: registerDetail.location,
              is_active: registerDetail.is_active,
              steps_config: registerDetail.steps_config || [],
              workflow_enabled: registerDetail.workflow_enabled,
              review_required: registerDetail.review_required,
              approval_required: registerDetail.approval_required,
              otp_required: registerDetail.otp_required,
              qr_required: registerDetail.qr_required,
              allow_bulk_upload: registerDetail.allow_bulk_upload,
              allow_multiple_update: registerDetail.allow_multiple_update
            };
            this.registerService.createRegister(payload).subscribe({
              next: (created: any) => {
                if (fields.length === 0) {
                  this.toastr.success('Register copied successfully.', 'Success');
                  this.fetchRegisters(this.currentPage, this.recordsPerPage, this.searchText);
                  return;
                }
                const copyRequests = fields.map(field => {
                  const fieldPayload = {
                    register: created.id,
                    name: field.name,
                    field_key: field.field_key,
                    data_type: field.data_type,
                    is_mandatory: field.is_mandatory,
                    is_unique: field.is_unique,
                    step_number: field.step_number,
                    sequence: field.sequence,
                    min_length: field.min_length,
                    max_length: field.max_length,
                    is_numeric: field.is_numeric,
                    is_decimal: field.is_decimal,
                    cluster: field.cluster,
                    default_value: field.default_value,
                    help_text: field.help_text,
                    is_searchable: field.is_searchable,
                    is_filterable: field.is_filterable,
                    is_active: field.is_active
                  };
                  return this.fieldService.createField(fieldPayload);
                });
                forkJoin(copyRequests).subscribe({
                  next: () => {
                    this.toastr.success('Register copied successfully.', 'Success');
                    this.fetchRegisters(this.currentPage, this.recordsPerPage, this.searchText);
                  }
                });
              }
            });
          }
        });
      }
    });
  }

  onLocationFilterChange(): void {
    this.currentPage = 1;
    this.updateSearchExtras();
    this.fetchRegisters(this.currentPage, this.recordsPerPage, this.searchText);
  }

  updateSearchExtras(): void {
    this.searchExtras = {
      location_id: this.selectedLocation || ''
    };
  }

  next(): void {
    if (this.nextPage) {
      this.currentPage += 1;
      this.fetchRegisters(this.currentPage, this.recordsPerPage, this.searchText);
    }
  }

  previous(): void {
    if (this.previousPage) {
      this.currentPage -= 1;
      this.fetchRegisters(this.currentPage, this.recordsPerPage, this.searchText);
    }
  }

  selectPageSize(): void {
    this.currentPage = 1;
    this.fetchRegisters(this.currentPage, this.recordsPerPage, this.searchText);
  }

    onPageChange(page: number): void {
        this.currentPage = page;
        this.fetchRegisters(this.currentPage, this.recordsPerPage, this.searchText);
    }

    onPageSizeChange(pageSize: number): void {
        this.recordsPerPage = pageSize;
        this.currentPage = 1;
        this.fetchRegisters(this.currentPage, this.recordsPerPage, this.searchText);
    }

  get totalPages(): number {
    const totalPage = Math.ceil(this.totalRecords / this.recordsPerPage);
    return totalPage === 0 ? 1 : totalPage;
  }

  getLocationLabel(locationId: number | null): string {
    const resolvedId = (locationId as any)?.id || locationId;
    if (!resolvedId) {
      return 'Org';
    }
    const loc = this.locations.find(item => item.id === resolvedId);
    if (!loc) {
      return '-';
    }
    return `${loc.village_or_locality_name}, ${loc.district_name}`;
  }
}
